import "./index.css";

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Spin,
  Row,
  Col,
  Typography,
  Space,
  Collapse,
  Statistic,
} from "antd";
import { 
  SearchOutlined, 
  FilterOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CalendarOutlined,
  BankOutlined,
  BookOutlined,
  ToolOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import MenuLayout from "../../components/Menu";
import formFields from "./formFields";
import Loading from "../../components/Loading";
import { supabase } from "../../lib/supabase";
import MapRendererBMap from "./MapRendererBMap";

const { Title, Text } = Typography;

const PAGE_SIZE = 1000;

const initialQueryParams = {
  ...formFields.reduce((acc, item) => {
    acc[item.key] = null;
    return acc;
  }, {}),
  page: 1,
};

const AlumMap = () => {
  const navigate = useNavigate();
  const [alumniMapList, setAlumniMapList] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const queryParams = useRef(initialQueryParams);
  const [form] = Form.useForm();
  
  // Helper function to filter alumni with valid coordinates
  const getAlumniWithValidCoords = (list) => {
    return list.filter(item => 
      item.loc_coord != null && 
      item.loc_coord.lat != null && 
      item.loc_coord.lon != null
    );
  };
  
  // Initialize form with default values
  useEffect(() => {
    form.setFieldsValue(initialQueryParams);
  }, [form]);
  const [pagination, setPagination] = useState({
    total: 0,
    current: 1,
  });
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    query();
  }, [queryParams.current]);

  const handleFinishForm = (formValues) => {
    const transformedFormValues = Object.entries(formValues).reduce(
      (acc, [key, value]) => {
        // Handle numeric fields that should be integers
        if (['q_schoolm_start_time', 'q_schoolm_class', 'q_schoolh_start_time', 'q_schoolh_class'].includes(key)) {
          if (value === undefined || value === null || value === '') {
            acc[key] = null;
          } else {
            // Clean and parse numeric values, removing any non-numeric characters except negative sign
            const cleanedValue = String(value).replace(/[^\d-]/g, '');
            const parsedValue = parseInt(cleanedValue, 10);
            acc[key] = isNaN(parsedValue) ? null : parsedValue;
          }
        } else {
          acc[key] = value === undefined ? null : value;
        }
        return acc;
      },
      {}
    ); // set all undefined value to null and clean numeric fields

    queryParams.current = {
      ...transformedFormValues,
      page: 1,
    };
    setPagination((prev) => {
      return {
        ...prev,
        current: 1,
      };
    });
  };

  const handleClearForm = () => {
    form.resetFields();
    form.submit();
  };

  async function query() {
    setLoading(true);
    const { data, error } = await supabase.rpc("query_filter_map", {
      ...queryParams.current,
      qsize: PAGE_SIZE,
    });

    setAlumniMapList(data?.data || []);
    setPagination((prev) => {
      return {
        ...prev,
        total: data?.total || 0,
      };
    });
    setLoading(false);
  }

  const checkUserStatus = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate("/login");
    }
  };

  const fetchUserProfile = async () => {
    setUserLoading(true);
    const { data, error } = await supabase.rpc("query_self", {});
    
    if (data?.data?.[0]) {
      setUserProfile(data.data[0]);
    }
    setUserLoading(false);
  };

  const handleSameMiddleSchoolClass = () => {
    if (!userProfile) return;
    
    // Create the form values for middle school class search
    const formValues = {
      ...initialQueryParams,
      q_schoolm_start_time: userProfile.schoolm_start_time,
      q_schoolm_class: userProfile.schoolm_class,
    };
    
    // Update form and trigger submission
    form.setFieldsValue(formValues);
    handleFinishForm(formValues);
  };

  const handleSameHighSchoolClass = () => {
    if (!userProfile) return;
    
    // Create the form values for high school class search
    const formValues = {
      ...initialQueryParams,
      q_schoolh_start_time: userProfile.schoolh_start_time,
      q_schoolh_class: userProfile.schoolh_class,
    };
    
    // Update form and trigger submission
    form.setFieldsValue(formValues);
    handleFinishForm(formValues);
  };

  const handleAllAlumni = () => {
    // Clear all filters and show all alumni
    const formValues = { ...initialQueryParams };
    
    // Update form and trigger submission
    form.setFieldsValue(formValues);
    handleFinishForm(formValues);
  };

  useEffect(() => {
    checkUserStatus();
    fetchUserProfile();
  }, []);

  const getFieldIcon = (key) => {
    const iconMap = {
      q_name: <UserOutlined />,
      q_loc: <EnvironmentOutlined />,
      q_year: <CalendarOutlined />,
      q_school: <BankOutlined />,
      q_major: <BookOutlined />,
      q_schoolm_start_time: <CalendarOutlined />,
      q_schoolm_class: <TeamOutlined />,
      q_schoolh_start_time: <CalendarOutlined />,
      q_schoolh_class: <TeamOutlined />,
      q_career: <ToolOutlined />,
      q_job: <ToolOutlined />,
    };
    return iconMap[key] || <UserOutlined />;
  };

  const getFormItem = (item) => {
    if (item.type === "input") {
      return (
        <Form.Item 
          label={
            <Space>
              {getFieldIcon(item.key)}
              <Text strong>{item.label}</Text>
            </Space>
          } 
          key={item.key} 
          name={item.key}
          className="modern-form-item"
        >
          <Input 
            size="large"
            className="modern-input"
            placeholder={`搜索${item.label}...`}
          />
        </Form.Item>
      );
    }

    if (item.type === "select" && item.options) {
      return (
        <Form.Item 
          label={
            <Space>
              {getFieldIcon(item.key)}
              <Text strong>{item.label}</Text>
            </Space>
          } 
          key={item.key} 
          name={item.key}
          className="modern-form-item"
        >
          <Select 
            className="modern-select" 
            options={item.options} 
            allowClear 
            size="large"
            placeholder={`选择${item.label}`}
          />
        </Form.Item>
      );
    }

    return null;
  };

  // All fields are now in the advanced filters
  const allFields = formFields;

  return (
    <MenuLayout>
      <div className="map-container">
        {/* Statistics Cards */}
        <Row gutter={[16, 16]} className="stats-section">
          <Col xs={24} sm={12}>
            <Card className="stat-card">
              <Statistic
                title="地图校友数"
                value={getAlumniWithValidCoords(alumniMapList).length}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ color: 'var(--primary-color)' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card className="stat-card">
              <Statistic
                title="覆盖城市"
                value={new Set(getAlumniWithValidCoords(alumniMapList).map(item => item.loc_city).filter(Boolean)).size}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* User Shortcuts Section */}
        {userProfile && (
          <div className="user-shortcuts-section">
            <Card className="user-shortcuts-card">
              <div className="user-shortcuts-header">
                <Title level={4}>
                  <TeamOutlined /> 快速查找
                </Title>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<TeamOutlined />}
                    onClick={handleAllAlumni}
                    loading={loading}
                  >
                    全部校友
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<TeamOutlined />}
                    onClick={handleSameMiddleSchoolClass}
                    loading={loading}
                  >
                    同初中班级 ({userProfile.schoolm_start_time}级{userProfile.schoolm_class}班)
                  </Button>
                  <Button 
                    type="primary" 
                    icon={<TeamOutlined />}
                    onClick={handleSameHighSchoolClass}
                    loading={loading}
                  >
                    同高中班级 ({userProfile.schoolh_start_time}级{userProfile.schoolh_class}班)
                  </Button>
                </Space>
              </div>
            </Card>
          </div>
        )}

        {/* Advanced Filters Section */}
        <div className="advanced-filters-section">
            <Collapse 
              ghost 
              expandIconPosition="end"
              className="filters-collapse"
              activeKey={filtersExpanded ? ["1"] : []}
              onChange={(keys) => setFiltersExpanded(keys.includes("1"))}
              items={[
                {
                  key: "1",
                  label: (
                    <span className="filters-header">
                      <FilterOutlined />
                      高级筛选
                    </span>
                  ),
                  children: (
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleFinishForm}
                      className="advanced-filters-form"
                    >
                      <Row gutter={[24, 16]}>
                        {allFields.map((item) => (
                          <Col xs={24} sm={12} md={8} lg={6} key={item.key}>
                            {getFormItem(item)}
                          </Col>
                        ))}
                      </Row>
                      <div className="filter-actions">
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                          应用筛选
                        </Button>
                        <Button onClick={handleClearForm}>
                          清空筛选
                        </Button>
                      </div>
                    </Form>
                  )
                }
              ]}
            />
          </div>

        {/* Map Section */}
        <div className="map-section">
          <Card 
            title={
              <Space>
                <EnvironmentOutlined />
                <Text strong>校友分布地图</Text>
              </Space>
            }
            className="map-card"
            bordered={false}
            bodyStyle={{ padding: 0 }}
          >
            <Spin 
              tip="加载地图中..." 
              indicator={Loading} 
              spinning={loading}
              className="map-spinner"
            >
              <MapRendererBMap alumniMapList={alumniMapList} userProfile={userProfile}/>
            </Spin>
          </Card>
        </div>
      </div>
    </MenuLayout>
  );
};

export default AlumMap;
